import React, { useState } from 'react';
import { ChevronRight, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { rejectedJsasAuthoredBy, rejectionReasonText } from '../lib/jsa';
import { LoadFailureNotice } from './ui/LoadFailureNotice';
import JSADetailModal from './JSADetailModal';

/**
 * THE AUTHOR'S HALF OF THE RESUBMIT LOOP, on the field safety screens.
 *
 * Both field screens counted draft and submitted JSAs and nothing else, so a
 * rejected JSA vanished from the view of the one person expected to fix it.
 * Closing the loop needed someone in the office to notice and go tell them.
 * This lists the reader's OWN rejected JSAs with the reason, and opens the
 * detail modal, which offers Resubmit (the API admits the author, api#811).
 *
 * Scoped to the author on purpose: GET /safety/jsa is the whole tenant's list,
 * and a crew hand being shown everyone else's rejections is noise, not a
 * prompt to act.
 *
 * THREE STATES. Nothing rejected renders nothing, because this is a prompt and
 * not a register. A failed list read is NOT nothing: it renders the failure
 * where the prompt would have been, or a hand in a dead zone would be told by
 * omission that none of their JSAs came back.
 */
export default function RejectedJsasPanel({ jsas, error }: { jsas: any[] | undefined; error: unknown }) {
  const { user } = useAuth();
  const [openId, setOpenId] = useState<string | null>(null);

  if (error) {
    return (
      <section aria-label="Your rejected JSAs" className="bg-fieldiq-card rounded-xl border border-fieldiq-border">
        <LoadFailureNotice error={error} subject="JSAs" className="py-6" />
      </section>
    );
  }

  const mine = rejectedJsasAuthoredBy(jsas ?? [], user?.id);
  if (mine.length === 0) return null;

  return (
    <section aria-labelledby="rejected-jsas-heading">
      <h3 id="rejected-jsas-heading" className="text-sm font-bold text-white mb-3 flex items-center gap-2">
        <XCircle size={14} className="text-fieldiq-danger" aria-hidden="true" />
        Your JSAs sent back ({mine.length})
      </h3>
      <ul className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden divide-y divide-fieldiq-border">
        {mine.map((j: any) => (
          <li key={j.id} className="border-l-4 border-l-fi-red">
            <button
              type="button"
              onClick={() => setOpenId(j.id)}
              className="w-full text-left px-4 py-3 flex items-start gap-3"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">
                  {j.title || 'Untitled JSA'}
                  {j.jobNumber ? <span className="font-normal text-fieldiq-muted"> · {j.jobNumber}</span> : null}
                </p>
                <p className="text-xs text-fieldiq-text mt-1">
                  <span className="font-semibold">Rejected:</span> {rejectionReasonText(j.rejectionReason ?? j.rejection_reason)}
                </p>
                <p className="text-[11px] text-fieldiq-muted mt-1">Review and resubmit</p>
              </div>
              <ChevronRight size={16} className="text-fieldiq-muted mt-0.5 flex-shrink-0" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      <JSADetailModal jsaId={openId} isOpen={!!openId} onClose={() => setOpenId(null)} />
    </section>
  );
}
